import React, { useMemo, useState } from 'react';
import { createAuthDependencies } from '@/app/auth/createAuthDependencies';
import { useAppTranslation } from '@/i18n/useAppTranslation';
import { LocaleSelect } from '@/presentation/dashboard/LocaleSelect';
import { formatApiErrorMessage } from '@/utils/formatApiErrorMessage';

export default function LoginPage({ onRegister, onLoggedIn }) {
  const { t, locale, setLocale } = useAppTranslation();
  const deps = useMemo(() => createAuthDependencies(), []);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetOpen, setResetOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetResult, setResetResult] = useState('');

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    if (!email || !password) {
      setError(t('auth.login.missingFields'));
      return;
    }
    setLoading(true);
    try {
      const result = await deps.loginWithEmail.execute({ email, password });
      onLoggedIn?.(result);
    } catch (e) {
      setError(formatApiErrorMessage(e?.message, t) || t('auth.login.failed'));
    } finally {
      setLoading(false);
    }
  };

  const submitResetPassword = async () => {
    setResetResult('');
    if (!resetEmail) {
      setResetResult(t('auth.reset.missingEmail'));
      return;
    }
    setResetLoading(true);
    try {
      const result = await deps.requestPasswordReset.execute({ email: resetEmail });
      if (result?.mode === 'admin-auto-reset-dev') {
        setResetResult(t('auth.reset.devAdminReset'));
      } else {
        setResetResult(t('auth.reset.sent'));
      }
    } catch (e) {
      setResetResult(formatApiErrorMessage(e?.message, t) || t('auth.reset.failed'));
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-sky-50 p-4 md:p-8">
      <div className="pointer-events-none fixed right-4 top-4 z-50 pointer-events-auto">
        <LocaleSelect value={locale} onChange={setLocale} ariaLabel={t('auth.langSwitch')} />
      </div>
      <div className="grid w-full max-w-6xl grid-cols-1 overflow-hidden rounded-[2rem] border border-sky-100 bg-sky-100/70 shadow-2xl md:grid-cols-2">
        <section className="relative hidden overflow-hidden p-10 md:flex md:flex-col md:justify-between">
          <div>
            <h2 className="font-headline text-5xl font-extrabold leading-tight text-slate-900">
              {t('auth.login.heroTitleA')}{' '}
              <span className="text-emerald-700">{t('auth.login.heroTitleB')}</span>
              {t('auth.login.heroTitleC')}
            </h2>
            <p className="mt-5 max-w-md text-slate-600">
              {t('auth.login.heroSubtitle')}
            </p>
          </div>
          <img
            className="aspect-[4/3] w-full rounded-[2rem] border-4 border-white object-cover shadow-xl"
            src="https://lh3.googleusercontent.com/aida-public/AB6AXuAt6hoZEQ4xh3Quvr-YwITxBRBYqwkja4mlCN1ZCwTGKn9OhvH3AJOb9XLiwRRUPZnkjqTbOV7rRqBOlPDVQ5AN2K2kR-F-n9tPJ9V9cOltOgzTp-mSuFoQNqX0500qxafWffOuUv9n5173_f6DnVQQdD4cf8jACXvNQPzY_MQe_-A7mVY_lVDyn85mf7E4bPyP1GDPquu94tP_-0bJV96scqATROIvjYUFCQ5NKYpiPe_SjlCxHEAtwFwxtMU_4uwkxkPrVqATcc8"
            alt={t('auth.login.heroImageAlt')}
          />
        </section>

        <section className="bg-white p-8 md:p-14">
          <div className="mx-auto w-full max-w-md">
            <h3 className="font-headline text-3xl font-extrabold text-slate-900">{t('auth.login.title')}</h3>
            <p className="mt-2 text-slate-600">{t('auth.login.subtitle')}</p>

            <form onSubmit={submit} className="mt-8 space-y-5">
              <label className="block">
                <span className="mb-2 block text-sm font-bold text-slate-700">{t('auth.login.emailLabel')}</span>
                <div className="relative">
                  <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">mail</span>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full rounded-xl border-0 bg-sky-50 py-3.5 pl-11 pr-4 focus:ring-2 focus:ring-emerald-300"
                    placeholder={t('auth.login.emailPlaceholder')}
                  />
                </div>
              </label>
              <label className="block">
                <div className="mb-2 flex items-center justify-between">
                  <span className="block text-sm font-bold text-slate-700">{t('auth.login.passwordLabel')}</span>
                  <button
                    type="button"
                    onClick={() => {
                      setResetOpen(true);
                      setResetEmail(email);
                      setResetResult('');
                    }}
                    className="text-xs font-bold text-cyan-700 hover:underline"
                  >
                    {t('auth.login.forgotPassword')}
                  </button>
                </div>
                <div className="relative">
                  <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">lock</span>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full rounded-xl border-0 bg-sky-50 py-3.5 pl-11 pr-4 focus:ring-2 focus:ring-emerald-300"
                    placeholder="••••••••"
                  />
                </div>
              </label>

              {error ? <p className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p> : null}

              <button
                disabled={loading}
                className="w-full rounded-xl bg-emerald-700 py-4 text-lg font-bold text-white shadow-lg shadow-emerald-200 transition hover:bg-emerald-800 disabled:opacity-50"
                type="submit"
              >
                {loading ? t('auth.login.loading') : t('auth.login.submit')}
              </button>
            </form>

            <p className="mt-8 text-center text-slate-600">
              {t('auth.login.noAccount')}{' '}
              <button onClick={onRegister} className="font-bold text-emerald-700 hover:underline">
                {t('auth.login.registerCta')}
              </button>
            </p>
          </div>
        </section>
      </div>
      {resetOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl">
            <div className="mb-3 flex items-center justify-between">
              <h4 className="font-headline text-xl font-extrabold text-slate-900">{t('auth.reset.title')}</h4>
              <button
                onClick={() => setResetOpen(false)}
                className="rounded-full p-1 text-slate-500 hover:bg-slate-100"
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <p className="text-sm text-slate-600">
              {t('auth.reset.body')}
            </p>
            <input
              type="email"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              className="mt-4 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:ring-2 focus:ring-cyan-300"
              placeholder={t('auth.login.emailPlaceholder')}
            />
            {resetResult ? (
              <p className="mt-3 rounded-lg bg-sky-50 px-3 py-2 text-sm font-medium text-sky-800">
                {resetResult}
              </p>
            ) : null}
            <button
              type="button"
              onClick={submitResetPassword}
              disabled={resetLoading}
              className="mt-4 w-full rounded-xl bg-cyan-700 py-3 text-sm font-bold text-white hover:bg-cyan-800 disabled:opacity-50"
            >
              {resetLoading ? t('auth.reset.loading') : t('auth.reset.submit')}
            </button>
          </div>
        </div>
      ) : null}
    </main>
  );
}

